import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  MenuItem,
  Modal,
  FormControl,
} from "@mui/material";
import { Close, Person, Email } from "@mui/icons-material";
import { TbPhoneCall } from "react-icons/tb";
import { gsap } from "gsap";
import { styled } from "@mui/system";
import { popupimg } from "../../../assets";
import { useMutation } from "react-query";
import { createLead } from "../../../api/services";
import { toast } from "react-toastify";
import CustomSelect from "../../../Components/Select/CustomSelect";
import Cookies from "js-cookie";

// Updated Validation Schema
const schema = yup.object().shape({
  name: yup.string().required("Name is required").min(3, "Name must be at least 3 characters"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: yup
    .string()
    .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
    .required("Phone number is required"),
  message: yup.string().optional(),
});

const InputContainer = styled(Box)({
  position: "relative",
  width: "100%",
  marginBottom: "25px",
  display: "flex",
  alignItems: "center",
});

const StyledInput = styled(TextField)({
  width: "100%",
  height: "45px",
  backgroundColor: "transparent",
  border: "1px solid rgba(255, 255, 255, 0.6)",
  borderRadius: "25px",
  color: "white",
  fontSize: "16px",
  paddingLeft: "5px",
  "& input": {
    paddingLeft: "50px",
    height: "10px",
    color: "white",
    border: "none",
  },
  "& .MuiOutlinedInput-root": {
    padding: "0px",
    "& fieldset": {
      border: "none",
      width: "100%",
      height: "45px",
      backgroundColor: "transparent",
    },
    "&:hover fieldset": {
      border: "none",
      width: "100%",
      height: "45px",
      backgroundColor: "transparent",
    },
    "&.Mui-focused fieldset": {
      border: "none",
      width: "100%",
      height: "45px",
      backgroundColor: "transparent",
    },
  },
});

const StyledSelect = styled(TextField)({
  width: "100%",
  height: "45px",
  backgroundColor: "transparent",
  borderRadius: "25px",
  color: "white",
  fontSize: "16px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.6) !important",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#9f8054 !important",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#9f8054 !important",
    boxShadow: "0px 4px 10px rgba(159, 128, 84, 0.6)",
  },
});

const InputIcon = styled(Box)({
  position: "absolute",
  left: "16px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#9f8054",
  fontSize: "22px",
  display: "flex",
  alignItems: "center",
});

interface FormData {
  name: string;
  email: string;
  phoneNumber: string;
  message?: string;
}

interface LeadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LeadModal: React.FC<LeadModalProps> = ({ isOpen, onClose }) => {
  const popupRef = useRef<HTMLDivElement | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {},
  });

  const mutation = useMutation(createLead, {
    onSuccess: (response: any) => {
      Cookies.set("formFilled", "true", { expires: 30 });
      toast.success(response.message || 'Thank you for visiting. Our sales team will contact you soon.'); // Use message from backend
      reset();
      onClose();
    },
    onError: () => {
      toast.error("Please check your network connection and try again.");
    },
  });

  useEffect(() => {
    if (isOpen) {
      gsap.fromTo(
        popupRef.current,
        { scale: 0.7, opacity: 0, rotateY: 30 },
        { scale: 1, opacity: 1, rotateY: 0, duration: 0.4, ease: "power3.out" }
      );
    } else {
      gsap.to(popupRef.current, {
        scale: 0.7,
        opacity: 0,
        rotateY: 30,
        duration: 0.6,
        ease: "power3.in",
      });
    }
  }, [isOpen]);

  const onSubmit = (data: FormData) => {
    const newdata = {
      ...data,
      type: "Customer",
      sourceType: "Online",
      location: "Hyderabad",
      projectName: "LUXOR EXCLUSIVE",
      gender: 'Male',
    };
    mutation.mutate(newdata);
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        ref={popupRef}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "650px", md: "800px" },
          display: "flex",
          overflow: "hidden",
          bgcolor: "rgba(255, 255, 255, 0.15)",
          backdropFilter: "blur(20px)",
          borderRadius: "10px",
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            flex: 1,
            backgroundImage: `url(${popupimg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: { xs: "none", md: "block" },
          }}
        />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "50px 40px",
            background: "rgba(15, 15, 15, 0.7)",
            backdropFilter: "blur(18px)",
            color: "white",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: "15px",
              right: "15px",
              bgcolor: "#9f8054",
              color: "#fff",
              width: "35px",
              height: "35px",
              borderRadius: "50%",
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: "#7f6540",
                transform: "scale(1.1) rotate(90deg)",
              },
            }}
          >
            <Close />
          </IconButton>
          <Typography
            variant="h5"
            sx={{ fontWeight: "600", mb: 3, color: "#9f8054" }}
          >
            Book Your Site Visit for Luxor Exclusive
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            <InputContainer>
              <InputIcon>
                <Person />
              </InputIcon>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <StyledInput
                    {...field}
                    variant="outlined"
                    placeholder="Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </InputContainer>


            <InputContainer>
              <InputIcon>
                <Email />
              </InputIcon>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <StyledInput
                    {...field}
                    variant="outlined"
                    placeholder="Email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </InputContainer>



            <InputContainer>
              <InputIcon>
                <TbPhoneCall />
              </InputIcon>
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <StyledInput
                    {...field}
                    variant="outlined"
                    placeholder="Phone Number"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                  />
                )}
              />
            </InputContainer>

            {/* <InputContainer>
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <StyledInput
                    {...field}
                    variant="outlined"
                    placeholder="Message (Optional)"
                    error={!!errors.message}
                    helperText={errors.message?.message}
                  />
                )}
              />
            </InputContainer> */}

            
          
            <Button
              type="submit"
              variant="contained"
              sx={{
              mt: 2,
              width: "100%",
              bgcolor: "#9f8054",
              borderRadius: "25px",
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: "#7f6540",
                transform: "scale(1.05)",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
              }}
            >
              Submit
            </Button>
          </form>
        </Box>
      </Box>
    </Modal>
  );
};

export default LeadModal;
